import * as THREE from "three";
import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";
import type { LevelEntity } from "../types/level_entity.js";
import { SceneManager } from "../core/scene_manager.js";
import { Renderer } from "../core/renderer.js";

const PIECE = {
  Cube: 1,
  Stone: 2,
  Jar: 3,
  Cone: 4,
  Metal: 5,
  Ice: 6,
  Tree: 7,
  Watermelon: 8,
  Plastic: 9,
  RoundMetal: 10,
  IronCan: 11,
  Column: 12,
  RoundColumn: 13,
} as const;

const SUPER_HARD = 2;

const LABEL_MODES = [ "OFF", "SHORT", "FULL" ];

const SVG_NS = "http://www.w3.org/2000/svg";

const COMB_BOTTOM_RATIO = 0.47;

const COLS = 9;
const LAYERS = 3;
const COLUMN_HEIGHT = 2;

export interface ShowcaseType
{
  type: number;
  name: string;
  entity: string;
  variants: number;
  skip?: number[];
  on_top?: boolean;
}

export const TYPES: ShowcaseType[] = [
  { type: PIECE.Cube, name: "Cube", entity: "PhysicsCube", variants: 3 },
  { type: PIECE.Stone, name: "Stone", entity: "PhysicsCube STONE", variants: 1 },
  { type: PIECE.Column, name: "Column", entity: "PhysicsCube COLUMN", variants: 1 },
  { type: PIECE.RoundColumn, name: "RoundColumn", entity: "PhysicsCube ROUND_COLUMN", variants: 1 },
  { type: PIECE.Plastic, name: "Plastic", entity: "PhysicsCube PLASTIC", variants: 8 },
  { type: PIECE.Jar, name: "Jar", entity: "JamJar", variants: 4 },
  { type: PIECE.Metal, name: "Metal", entity: "MetalBlock Square", variants: 7 },
  { type: PIECE.RoundMetal, name: "RoundMetal", entity: "MetalBlock Round", variants: 7 },
  { type: PIECE.IronCan, name: "IronCan", entity: "MetalBlock Iron", variants: 2 },
  { type: PIECE.Ice, name: "Ice", entity: "IceBlock", variants: 1 },
  { type: PIECE.Tree, name: "Tree", entity: "TreeBlock", variants: 1 },
  { type: PIECE.Cone, name: "Cone", entity: "PhysicsCone", variants: 11, skip: [ 2 ], on_top: true },
  { type: PIECE.Watermelon, name: "Watermelon", entity: "WatermelonBlock", variants: 1, on_top: true },
];

interface Entry
{
  type: number;
  variant?: number;
  short: string;
  full: string;
}

function buildEntries(): { stacked: Entry[]; toppers: Entry[] }
{
  const stacked: Entry[] = [];
  const toppers: Entry[] = [];

  for ( const type of TYPES )
  {
    for ( let v = 0; v < type.variants; v++ )
    {
      if ( type.skip?.includes( v ) )
        continue;

      const variant = type.variants > 1 ? v : undefined;
      const short = `${type.name}${variant === undefined ? "" : `\nv${variant}`}`;
      const entry: Entry = {
        type: type.type,
        variant,
        short,
        full: `${short}\n${type.entity.replace( " ", "\n" )}`,
      };

      ( type.on_top ? toppers : stacked ).push( entry );
    }
  }

  return { stacked, toppers };
}

interface Label
{
  short: string;
  full: string;
  slot_col: number;
  slot_row: number;
}

interface Showcase
{
  layers: LevelLayer[];
  labels: Map<number, Label>;
  rows: number;
}

function buildShowcase(): Showcase
{
  const { stacked, toppers } = buildEntries();

  const columns: Entry[][][] = Array.from(
    { length: LAYERS },
    () => Array.from( { length: COLS }, (): Entry[] => [] )
  );

  let taken = 0;
  let topped = 0;

  for ( let layer = 0; layer < LAYERS; layer++ )
    for ( let col = 0; col < COLS && taken < stacked.length; col++ )
      columns[layer][col].push( stacked[taken++] );

  for ( let layer = 0; layer < LAYERS; layer++ )
    for ( let col = 0; col < COLS; col++ )
    {
      if ( columns[layer][col].length === 0 )
        continue;

      if ( taken < stacked.length )
        columns[layer][col].push( stacked[taken++] );
      else if ( topped < toppers.length )
        columns[layer][col].push( toppers[topped++] );
    }

  const missed = stacked.length - taken + toppers.length - topped;
  if ( missed > 0 )
    console.warn( `[BlockShowcase] ${missed} blocks did not fit ${LAYERS}x${COLS}x${COLUMN_HEIGHT}` );

  const labels = new Map<number, Label>();
  let next_id = 1;

  const toLayer = ( layer_columns: Entry[][], layer: number ): LevelLayer =>
  {
    const rows = Math.max( 1, ...layer_columns.map( column => column.length ) );
    const grid: number[][] = Array.from( { length: rows }, () => Array( COLS ).fill( 0 ) );
    const pieces: LevelPiece[] = [];

    layer_columns.forEach( ( column, x ) =>
    {
      column.forEach( ( entry, height ) =>
      {
        const y = rows - 1 - height;
        const id = next_id++;

        grid[y][x] = id;
        pieces.push( {
          id,
          type: entry.type as LevelPieceType,
          x,
          y,
          width: 1,
          height: 1,
          variant: entry.variant,
        } );
        labels.set( id, {
          short: entry.short,
          full: entry.full,
          slot_col: x,
          slot_row: LAYERS * COLUMN_HEIGHT - 1 - layer * COLUMN_HEIGHT - ( COLUMN_HEIGHT - 1 - height ),
        } );
      } );
    } );

    return { grid, pieces };
  };

  return { layers: columns.map( toLayer ), labels, rows: LAYERS * COLUMN_HEIGHT };
}

const world_point = new THREE.Vector3();

export class BlockShowcase
{
  public static readonly LEVEL = 90100;

  private static active = false;
  private static showcase: Showcase | null = null;
  private static config: LevelConfig | null = null;

  private static root: HTMLDivElement | null = null;
  private static lines: SVGSVGElement | null = null;
  private static live: {
    entity: LevelEntity;
    el: HTMLDivElement;
    line: SVGLineElement;
    label: Label;
    half_width: number;
  }[] = [];
  private static label_mode = 1;

  public static isShowcaseLevel( level: number ): boolean
  {
    return level === this.LEVEL;
  }

  public static isActive(): boolean
  {
    return this.active;
  }

  public static blockCount(): number
  {
    return TYPES.reduce( ( sum, type ) => sum + type.variants - ( type.skip?.length ?? 0 ), 0 );
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static beginLevel(): void
  {
    this.active = true;
    this.clearLabels();
  }

  public static deactivate(): void
  {
    this.active = false;
    this.clearLabels();
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    if ( !this.isShowcaseLevel( level ) )
      return null;

    if ( this.config === null )
    {
      this.showcase = buildShowcase();
      this.config = {
        level: this.LEVEL,
        difficulty: SUPER_HARD as LevelConfig["difficulty"],
        layers: this.showcase.layers,
      };
    }

    return this.config;
  }

  public static labelModeName(): string
  {
    return LABEL_MODES[this.label_mode];
  }

  public static cycleLabels(): void
  {
    this.label_mode = ( this.label_mode + 1 ) % LABEL_MODES.length;

    if ( this.root )
      this.root.style.display = this.label_mode === 0 ? "none" : "block";

    for ( const entry of this.live )
    {
      entry.el.textContent = this.label_mode === 2 ? entry.label.full : entry.label.short;
      entry.half_width = entry.el.offsetWidth * 0.5;
    }
  }

  public static attachLabel( piece_id: number, entity: LevelEntity | undefined ): void
  {
    if ( !this.active || entity === undefined )
      return;

    const label = this.showcase?.labels.get( piece_id );
    if ( label === undefined )
      return;

    const root = this.ensureRoot();

    const el = document.createElement( "div" );
    el.textContent = this.label_mode === 2 ? label.full : label.short;
    el.style.cssText = [
      "position:absolute",
      "transform:translate(-50%,-100%)",
      "padding:1px 3px",
      "border-radius:4px",
      "background:rgba(8,10,18,0.82)",
      "color:#ffe27a",
      "font:700 10px/1.15 monospace",
      "white-space:pre",
      "text-align:center",
      "pointer-events:none",
      "text-shadow:0 1px 2px #000",
    ].join( ";" );

    const line = document.createElementNS( SVG_NS, "line" );
    line.setAttribute( "stroke", "rgba(255,226,122,0.55)" );
    line.setAttribute( "stroke-width", "1" );

    root.appendChild( el );
    this.lines!.appendChild( line );
    this.live.push( { entity, el, line, label, half_width: el.offsetWidth * 0.5 } );
  }

  public static updateLabels(): void
  {
    if ( !this.active || this.live.length === 0 || this.label_mode === 0 )
      return;

    const camera = SceneManager.camera;
    const showcase = this.showcase;
    if ( !camera || showcase === null )
      return;

    const bounds = Renderer.getCanvasBounds();
    const row_step = this.label_mode === 2 ? 52 : 30;
    const comb_bottom = bounds.top + bounds.height * COMB_BOTTOM_RATIO;
    const comb_top = comb_bottom - ( showcase.rows - 1 ) * row_step;
    const cell = bounds.width / COLS;

    for ( let i = this.live.length - 1; i >= 0; i-- )
    {
      const { entity, el, line, label, half_width } = this.live[i];

      if ( !entity.isAlive() || entity.mesh.parent === null )
      {
        el.remove();
        line.remove();
        this.live.splice( i, 1 );
        continue;
      }

      entity.mesh.getWorldPosition( world_point );
      world_point.y += entity.size.y * 0.5;
      world_point.project( camera );

      const anchor_x = bounds.left + ( world_point.x * 0.5 + 0.5 ) * bounds.width;
      const anchor_y = bounds.top + ( -world_point.y * 0.5 + 0.5 ) * bounds.height;

      const slot_x = Math.min(
        Math.max( bounds.left + ( label.slot_col + 0.5 ) * cell, bounds.left + half_width ),
        bounds.right - half_width
      );
      const slot_y = comb_top + label.slot_row * row_step;

      if ( world_point.z > 1 )
      {
        el.style.visibility = "hidden";
        line.setAttribute( "stroke-opacity", "0" );
        continue;
      }

      el.style.visibility = "visible";
      el.style.left = `${slot_x}px`;
      el.style.top = `${slot_y}px`;

      line.setAttribute( "stroke-opacity", "1" );
      line.setAttribute( "x1", `${slot_x}` );
      line.setAttribute( "y1", `${slot_y}` );
      line.setAttribute( "x2", `${anchor_x}` );
      line.setAttribute( "y2", `${anchor_y}` );
    }
  }

  public static clearLabels(): void
  {
    for ( const { el, line } of this.live )
    {
      el.remove();
      line.remove();
    }

    this.live = [];

    this.root?.remove();
    this.root = null;
    this.lines = null;
  }

  private static ensureRoot(): HTMLDivElement
  {
    if ( this.root )
      return this.root;

    const root = document.createElement( "div" );
    root.id = "blockShowcaseLabels";
    root.style.cssText = [
      "position:fixed",
      "left:0",
      "top:0",
      "right:0",
      "bottom:0",
      "z-index:2147483000",
      "pointer-events:none",
      this.label_mode === 0 ? "display:none" : "display:block",
    ].join( ";" );

    const lines = document.createElementNS( SVG_NS, "svg" );
    lines.style.cssText = "position:absolute;left:0;top:0;width:100%;height:100%;overflow:visible";

    root.appendChild( lines );
    document.body.appendChild( root );

    this.root = root;
    this.lines = lines;

    return root;
  }
}
